"use strict";

Object.defineProperty(exports, "__esModule", {
    value: true
});
exports.default = getSectionValidationInfo;
/**
 * Searches for a section within the validation information based on provided page code, section code, and optional row ID.
 * Returns the count of errors and warnings for that section and its fields, and the first error message or warning message (if no errors are found).
 *
 * @param {Object} validationInfo - The validation information to search through.
 * @param {string} pageCode - The code of the page the section belongs to.
 * @param {string} sectionCode - The code of the section to find.
 * @param {string} [rowId] - Optional. The row ID of the section to find.
 *
 * @returns {Object} An object containing four properties: `errors` and `warnings` (the count of errors and warnings), and `error` and `warning` (the first error message and warning message, if any). If no matching section is found, `errors` and `warnings` will be 0, and `error` and `warning` will be null.
 *
 * @example
 * const result = getSectionValidationInfo(validationInfo, 'pageCode', 'sectionCode', 'rowId');
 * console.log(result.errors); // Logs the count of errors for the specified section
 * console.log(result.warnings); // Logs the count of warnings for the specified section
 * console.log(result.error); // Logs the first error message for the specified section, or null if there are no errors
 * console.log(result.warning); // Logs the first warning message for the specified section, or null if there are no warnings
 */
function getSectionValidationInfo(validationInfo, pageCode, sectionCode, rowId) {
    var errorCount = 0;
    var warningCount = 0;
    var firstErrorMessage = null;
    var firstWarningMessage = null;

    if (validationInfo && validationInfo.pages) {
        var _iteratorNormalCompletion = true;
        var _didIteratorError = false;
        var _iteratorError = undefined;

        try {
            for (var _iterator = validationInfo.pages[Symbol.iterator](), _step; !(_iteratorNormalCompletion = (_step = _iterator.next()).done); _iteratorNormalCompletion = true) {
                var page = _step.value;

                if (page.code === pageCode && page.sections) {
                    var _iteratorNormalCompletion2 = true;
                    var _didIteratorError2 = false;
                    var _iteratorError2 = undefined;

                    try {
                        for (var _iterator2 = page.sections[Symbol.iterator](), _step2; !(_iteratorNormalCompletion2 = (_step2 = _iterator2.next()).done); _iteratorNormalCompletion2 = true) {
                            var section = _step2.value;

                            // Check for rowId if provided
                            if (rowId && section.rowId && section.rowId !== rowId) {
                                continue;
                            }

                            if (section.code === sectionCode) {
                                // Count errors and warnings for the section
                                if (section.errors) {
                                    errorCount += section.errors.length;
                                    if (section.errors.length > 0 && firstErrorMessage === null) {
                                        firstErrorMessage = section.errors[0].message;
                                    }
                                }

                                if (section.warnings) {
                                    warningCount += section.warnings.length;
                                    if (section.warnings.length > 0 && firstWarningMessage === null && firstErrorMessage === null) {
                                        firstWarningMessage = section.warnings[0].message;
                                    }
                                }

                                // Count errors and warnings for the fields in the section
                                if (section.fields) {
                                    var _iteratorNormalCompletion3 = true;
                                    var _didIteratorError3 = false;
                                    var _iteratorError3 = undefined;

                                    try {
                                        for (var _iterator3 = section.fields[Symbol.iterator](), _step3; !(_iteratorNormalCompletion3 = (_step3 = _iterator3.next()).done); _iteratorNormalCompletion3 = true) {
                                            var field = _step3.value;

                                            if (field.errors) {
                                                errorCount += field.errors.length;
                                                if (field.errors.length > 0 && firstErrorMessage === null) {
                                                    firstErrorMessage = field.errors[0].message;
                                                }
                                            }

                                            if (field.warnings) {
                                                warningCount += field.warnings.length;
                                                if (field.warnings.length > 0 && firstWarningMessage === null && firstErrorMessage === null) {
                                                    firstWarningMessage = field.warnings[0].message;
                                                }
                                            }
                                        }
                                    } catch (err) {
                                        _didIteratorError3 = true;
                                        _iteratorError3 = err;
                                    } finally {
                                        try {
                                            if (!_iteratorNormalCompletion3 && _iterator3.return) {
                                                _iterator3.return();
                                            }
                                        } finally {
                                            if (_didIteratorError3) {
                                                throw _iteratorError3;
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    } catch (err) {
                        _didIteratorError2 = true;
                        _iteratorError2 = err;
                    } finally {
                        try {
                            if (!_iteratorNormalCompletion2 && _iterator2.return) {
                                _iterator2.return();
                            }
                        } finally {
                            if (_didIteratorError2) {
                                throw _iteratorError2;
                            }
                        }
                    }
                }
            }
        } catch (err) {
            _didIteratorError = true;
            _iteratorError = err;
        } finally {
            try {
                if (!_iteratorNormalCompletion && _iterator.return) {
                    _iterator.return();
                }
            } finally {
                if (_didIteratorError) {
                    throw _iteratorError;
                }
            }
        }
    }

    return { errors: errorCount, warnings: warningCount, error: firstErrorMessage, warning: firstWarningMessage };
}